import { type RefObject, useEffect, useRef, useState } from 'react';

/*
 * Where focus goes around a modal, and the page behind it. Radix moves focus into a dialog
 * and traps it there; these cover what it doesn't: the page behind a portalled modal goes
 * inert, and focus goes back to the opener, or somewhere sensible when the opener has gone
 * (a row in a sheet that handed over to another sheet; an install card the guide removed).
 * ui/Sheet uses them; the result poster and the install guide still call them directly.
 */

// --- The page behind a modal ------------------------------------------------------------

/** How many portalled modals are open: the page stays inert until the last one closes. */
let modals = 0;

/**
 * While a modal that's portalled out of `#root` is open (a sheet, the install guide), the
 * page behind it is `inert`: no screen reader's swipe, rotor or virtual cursor can wander
 * into it, and nothing in it takes focus or clicks (A11Y-11, UX-018). Counted, so a sheet
 * opening over a sheet doesn't wake the page when the top one closes. Call it before
 * `useReturnFocus`, so the page is live again by the time focus goes back into it.
 */
export function useModalBackground(open = true, rootId = 'root') {
	useEffect(() => {
		if (!open || typeof document === 'undefined') return;
		const root = document.getElementById(rootId);
		if (!root) return;
		modals++;
		root.setAttribute('inert', '');
		return () => {
			modals = Math.max(0, modals - 1);
			if (modals === 0) root.removeAttribute('inert');
		};
	}, [open, rootId]);
}

/**
 * Remembers what had focus when a modal opened (read as it renders open, before anything
 * goes inert) and puts it back when it closes: the button that opened it, without
 * scrolling the page. Works for a modal that mounts to open (a sheet) and for one that's
 * always mounted and opens with `open` (the poster): the opener is read each time `open`
 * turns true. If the opener has gone, or is inert by then, focus goes to `fallback()`.
 */
export function useReturnFocus(open = true, fallback?: () => HTMLElement | null) {
	const [seen, setSeen] = useState<{ open: boolean; opener: Element | null }>({
		open: false,
		opener: null,
	});
	if (seen.open !== open)
		setSeen({
			open,
			opener: open && typeof document !== 'undefined' ? document.activeElement : null,
		});
	const opener = seen.opener;
	const fallbackRef = useRef(fallback);
	fallbackRef.current = fallback;
	useEffect(() => {
		if (!open) return;
		return () => {
			const back = opener instanceof HTMLElement && opener !== document.body && opener.isConnected && !opener.closest('[inert]') ? opener : null;
			(back ?? fallbackRef.current?.() ?? null)?.focus({ preventScroll: true });
		};
	}, [open, opener]);
}

// --- Where focus goes when a dialog closes ----------------------------------------------

/** Each open dialog → what opened it (its own opener, or, opened from another dialog, that one's). */
const openers = new WeakMap<Element, Element>();

const live = (el: Element | null | undefined): HTMLElement | null =>
	el instanceof HTMLElement && el !== document.body && el.isConnected && !el.closest('[inert]') ? el : null;

/**
 * Somewhere sensible on the page when a dialog's opener has gone (Today's install card,
 * which the guide's Done removes): the page's heading, as a route change does, else ≡.
 */
export function pageFocusTarget(rootId = 'root'): HTMLElement | null {
	if (typeof document === 'undefined') return null;
	const root = document.getElementById(rootId);
	if (!root) return null;
	const h1 = [...root.querySelectorAll<HTMLElement>('h1')].find((h) => !h.closest('[aria-busy="true"], [inert]'));
	if (h1) {
		if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
		return h1;
	}
	return root.querySelector<HTMLElement>('[data-testid="menu-button"]');
}

/**
 * `useReturnFocus` for a dialog that mounts to open (a sheet, the install guide), which
 * also copes with its opener going away (QA4-3). Focus goes back, in order, to: `returnTo()`
 * if given; the opener; if the opener was a row in another sheet that has since closed
 * (the nest switcher → Invite people), whatever opened that sheet; then `trigger()` (the
 * sheet's Radix trigger, for a click that didn't focus it, or a sheet opened from code while
 * its button is on the page); and last the page's heading (`pageFocusTarget`). Call it after
 * `useModalBackground`, as `useReturnFocus`.
 */
export function useDialogReturnFocus(ref: RefObject<HTMLElement | null>, returnTo?: () => HTMLElement | null, trigger?: () => HTMLElement | null) {
	const [origin] = useState<Element | null>(() => {
		if (typeof document === 'undefined') return null;
		const here = document.activeElement;
		const host = here instanceof Element ? here.closest('[role="dialog"]') : null;
		return host ? (openers.get(host) ?? null) : here;
	});
	useEffect(() => {
		const el = ref.current;
		if (el && origin) openers.set(el, origin);
	}, [ref, origin]);
	useReturnFocus(true, () => returnTo?.() ?? live(origin) ?? live(trigger?.()) ?? pageFocusTarget());
}
